import { makeCancellableAPIRequest } from "./index";
import _, { isEmpty, isString, map } from "lodash";
import ColumnFilterComparison from "components/ColumnFilterComparison";
import moment from "moment";
import { parseOldDateFormatsToDate } from "utils/dateUtils";
import { convertRelativeToAbsolute } from "ac-components/ACDateRangeFilter";

const TicketsCreationAPI = {
    syncTicketStatus(showDefaultMessage = false){
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/sync-status`,
            undefined,
            showDefaultMessage
        )
    },
    createJiraComment(data){
        return makeCancellableAPIRequest(
            "POST",
            "/user/tickets/jira/comments",
            {
                data,
            }
        )
    },
    getJiraComments(queryParams){
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/comments`,
            {
                params: queryParams
            }
        )
    },
    getJiraTicketAttachments(params){
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/attachments`,
            {params}
        )
    },
    createJiraTicketForFinding(data){
        return makeCancellableAPIRequest(
            "POST",
            "/user/tickets/jira",
            {
                data,
            }
        )
    },
    getJiraTicketDetails(id){
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/${id}`,
        )
    },
    createJiraTicketForPolicy(data) {
        return makeCancellableAPIRequest(
            "POST",
            "/user/tickets/jira/group", 
            {
                data
            }
        )
    },
    getTicketDetails(data) {
        return makeCancellableAPIRequest(
            "POST",
            "/user/tickets/jira/group/details", 
            {
                data
            }
        )
    },
    getBulkTickets(data) {
        return makeCancellableAPIRequest(
            "POST",
            `/user/tickets/jira/group/bulk`,
            {
                data
            }
        )
    },
    getAllTickets(data) {
        const params = {
            ...data.filters,
            ..._.omit(data, ["filters", "sortColumn", "sortOrder"])
        }
        return makeCancellableAPIRequest(
            "POST",
            `/api/dashboard/finding/jira/ticket`,
            {
                data: modifyTicketFilters(params)
            }
        )
    },
    getAllTicketStats(params) {
        return makeCancellableAPIRequest(
            "POST",
            `/api/dashboard/jira/ticket/stats`,
            {
                data: params
            }
        )
    },
    getAllTicketsFilters() {
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/tickets-filters`,
        )
    },
    createBulkTickets(data) {
        return makeCancellableAPIRequest(
            "POST",
            `/user/tickets/jira/bulk`,
            {
                data
            }
        )
    },
    createBulkSingleTickets(data) {
        return makeCancellableAPIRequest(
            "POST",
            `/user/tickets/jira/single`,
            {
                data
            }
        )
    },
    uploadJiraAttachment(data){
        return makeCancellableAPIRequest(
            "POST",
            "/api/v2/tickets/attachment/signed-url",
            {
                data
            }
        )
    },
    jiraFindingDetail(data){
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/ticket",
            {
                data
            }
        ) 
    },
    attachToExisting(data) {
        return makeCancellableAPIRequest(
            "PUT",
            "/user/tickets/jira",
            {
                data
            }
        )
    },
    detachJiraTicketForFinding(data, showDefaultMessage = true) {
        return makeCancellableAPIRequest(
            "DELETE",
            "/user/tickets/jira",
            {
                data
            },
            showDefaultMessage
        )
    },
    getJiraTickets(params = {}) {
        return makeCancellableAPIRequest(
            "GET",
            "/user/tickets/jira/search",
            {
                params
            }
        )
    },
    getLoginDetails(ticketSystem, loginConfigId) {
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/configuration/login/${ticketSystem}/${loginConfigId}`,
        );
    },
    getCustomTicketFields(params) {
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/custom-fields`,
            {
                params: new URLSearchParams(_.omitBy(params, _.isUndefined))
            }
        );
    },
    getCustomTicketFieldsSuggestions({name, fieldLabel, ...params}) {
        // the component return search text as `name` and we need name as field label, hence swapping
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/field-suggestions`,
            {
                params: {
                    ...params,
                    name: fieldLabel,
                    value: name
                }
            }
        );
    },
    downloadCSV(queryParams = {}) {
        if(queryParams.createdAt){
            queryParams = {
                ...queryParams,
                ...ColumnFilterComparison.toDates(queryParams.createdAt)
            }
        }
        if(!isEmpty(queryParams.slaFilter)){
            queryParams.slaFilter = queryParams.slaFilter.map(f => f === "In Progress" ? "In_Progress" : f)
        }
        return makeCancellableAPIRequest(
            "POST",
            `/user/tickets/jira/download/csv`,
            {
                params: queryParams,
            }
        )
    },

    getServiceNowCallers(queryParams){
        return makeCancellableAPIRequest(
            "GET",
            `/user/tickets/jira/snowCallers`,
            {
                params: queryParams,
            }
        )
    },
    getCustomWidgetTicketStats(data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/tickets/jira/ticketStats/get`,
            {
                data: modifyTicketFilters(data),
            }
        )
    },
    getPluginDetail(data){
        return makeCancellableAPIRequest(
            "POST",
            "/api/v2/tickets/plugin/details",
            {
                data
            }
        ) 
    },
    getTicketTimelineStats(data){
        return makeCancellableAPIRequest(
            "POST",
            `/api/dashboard/ticket/saved-search/timeline`,
            {
                data,
            }
        );
    },
}

export default TicketsCreationAPI;

export const modifyTicketFilters = (filters) => {
    const dateFilters = ["foundOn", "dueDate", "resolvedDate"];
    dateFilters.forEach((key) => {
        const filterValues = filters[key];
        if(filterValues){
            let _startDate, _endDate;
            const values = parseOldDateFormatsToDate(key, filterValues);
            if(!isEmpty(values)){
                [_startDate, _endDate] = values.map(v => new Date(v).toUTCString());
            } else {
                [_startDate, _endDate] = map(filterValues, (value, i) => {
                    let epoch = value;
                    if(value === "now"){
                        epoch = Date.now();
                    } else if(isString(value) && !moment(value).isValid()){
                        epoch = convertRelativeToAbsolute(value, i === 0 ? "start" : "end");
                    }
                    return new Date(epoch).toUTCString();
                });
            }
            if(key === "foundOn"){
              filters.startDate = _startDate;
              filters.endDate = _endDate;
              delete filters.foundOn;
            } else if (key === "dueDate"){
                filters.dueDateStartDate = _startDate;
                filters.dueDateEndDate = _endDate;
                delete filters.dueDate;
            } else if (key === 'resolvedDate') {
                filters.resolvedDateStartDate = _startDate;
                filters.resolvedDateEndDate = _endDate;
                delete filters.resolvedDate;
            } else {
                filters[key] = [_startDate, _endDate];
            }
        }
    });

    if(filters.createdAt){
        filters = {
            ...filters,
            // created at should be in comparison format (<= 9, >= 10), age comparison
            ...ColumnFilterComparison.toDates(filters.createdAt)   
        }
    }

    return filters;
}
